import { useParams, Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, User, AlertTriangle } from "lucide-react";
import Footer from "@/components/Footer";
import { SCIENCE_ARTICLES } from "@/data/scienceArticles";

export default function ScienceArticle() {
  const { slug } = useParams();
  const post = SCIENCE_ARTICLES.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="bg-[#050505] min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="font-heading text-4xl text-white">Article Not Found</h1>
          <Link to="/" className="text-[#D4AF37] text-sm">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="science-article">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">The Science</span>
        </div>
      </nav>

      <main className="pt-28 pb-24 px-6">
        <article className="max-w-3xl mx-auto">
          <header className="mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">The Science Behind CLARITY</p>
            <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
              <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4" data-testid="science-article-title">
              {post.title}
            </h1>
            <div className="flex gap-2 mb-6">
              {post.tags.map((tag) => (
                <span key={tag} className="text-[9px] tracking-wider uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1 rounded-sm">{tag}</span>
              ))}
            </div>
            <div className="gold-line-left w-24" />
          </header>

          <div className="space-y-6" data-testid="science-article-content">
            {post.content.map((block, i) => {
              if (block.type === "p") return <p key={`${block.type}-${i}`} className="text-zinc-300 text-base leading-relaxed">{block.text}</p>;
              if (block.type === "h2") return <h2 key={`${block.type}-${i}`} className="font-heading text-2xl sm:text-3xl tracking-tight uppercase text-white mt-10 mb-2">{block.text}</h2>;
              if (block.type === "h3") return <h3 key={`${block.type}-${i}`} className="font-heading text-xl tracking-tight uppercase text-[#D4AF37] mt-8 mb-2">{block.text}</h3>;
              if (block.type === "quote") return (
                <blockquote key={`${block.type}-${i}`} className="border-l-2 border-[#D4AF37] pl-5 py-2 my-6 text-zinc-300 italic text-base leading-relaxed">
                  {block.text}
                </blockquote>
              );
              if (block.type === "list") return (
                <ul key={`list-${i}`} className="space-y-2 my-4">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-zinc-300 text-sm">
                      <ArrowRight className="w-3 h-3 text-[#D4AF37] mt-1.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
              return null;
            })}
          </div>

          {/* Disclaimer */}
          {post.disclaimer && (
            <div className="mt-12 bg-[#121212] border border-red-900/30 p-6 rounded-sm space-y-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="font-heading text-lg tracking-tight uppercase text-white">A Word Before You Start — Read This Properly</h3>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">
                I'm not a doctor, and I'll never pretend to be. These articles are my story plus a plain-language summary of the research — general information and education only, not medical advice, and no substitute for proper care from someone qualified. Your body, your history and your health are yours alone, so it's on you to check with a doctor or qualified health professional about what's right for you before you change your habits, start any protocol, or stop anything.
              </p>
              <div className="bg-red-900/20 border border-red-900/40 p-4 rounded-sm">
                <p className="text-red-300 text-sm font-semibold">IF YOU DRINK HEAVILY OR USE DRUGS, DO NOT QUIT SUDDENLY ON YOUR OWN.</p>
                <p className="text-red-400/80 text-sm mt-2">Stopping alcohol or some drugs cold turkey can be dangerous — even life-threatening. Withdrawal can cause seizures and worse. Get a doctor in your corner before you put it down.</p>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">
                If you're struggling or in crisis right now, reach out to a health professional or a local helpline. In Australia: <strong className="text-white">Lifeline 13 11 14</strong>. You don't have to white-knuckle it alone.
              </p>
            </div>
          )}

          {/* Other articles */}
          <div className="mt-16">
            <h3 className="font-heading text-xl tracking-tight uppercase text-white mb-6">More From The Science</h3>
            <div className="space-y-4">
              {SCIENCE_ARTICLES.filter((a) => a.slug !== slug).map((related) => (
                <Link
                  key={related.slug}
                  to={`/science/${related.slug}`}
                  className="block border border-white/10 hover:border-[#D4AF37]/30 p-4 rounded-sm transition-colors group"
                >
                  <h4 className="font-heading text-lg tracking-tight uppercase text-white group-hover:text-[#D4AF37] transition-colors">{related.title}</h4>
                  <p className="text-zinc-500 text-xs mt-1">{related.readTime} read &bull; {related.date}</p>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
